import {
    Alert,
    Autocomplete,
    Box,
    Chip,
    Grid,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    getPermittedSlotTypesPerResourceCategoryFilter,
    Reservation, ReservationStatus,
    SlotType
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import CDatePicker from "crqlar-react-common/dist/components/CDatePicker";
import {
    AgeCategory
} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import ReservationDetailsWrapper from "../ReservationDetailsWrapper";
import {
    useCurrentApp,
    useCurrentAppId
} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {
    useServicesForSector
} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {
    useAllResourceCategoriesForSector
} from "crqlar-react-common/dist/utils/hooks/resources/useAllResourceCategories";
import {
    useAllRootResourcesForSector,
    useResourcesWithAvailability
} from "crqlar-react-common/dist/utils/hooks/resources/useAllRootResources";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {
    getSlotTypeBackgroundColor,
    getSlotTypeColor
} from "crqlar-react-common/dist/utils/color.util";
import {getSlotTypeIcon} from "crqlar-react-common/dist/utils/icon.util";
import {isResourceAvailable} from "crqlar-react-common/dist/utils/resource.util";
import {
    useResourceReservations
} from "crqlar-react-common/dist/utils/hooks/reservations/useReservationsSchedule";
import {useReservationsState} from "../../../../store/reservations.reducer";
import {
    ServiceType
} from "crqlar-react-common/dist/models/service/serviceListRequest.dto";
import {addMinutes} from "crqlar-react-common/dist/utils/date.util";
import {serviceDetailedInfo} from "crqlar-react-common/dist/utils/service.util";
import {Rate} from "crqlar-react-common/dist/models/rate/rate.model";
import {
    autoAssignResourcesForService,
    releaseAutoAssignedResources
} from "../../../../utils/serviceResourceAutoAssign.util";

interface Props {
    reservation?: Reservation
    setReservation: (reservation?: Reservation) => void
    // availabilityForActiveReservation?: RestaurantAvailableTablesResponse
    // loadingAvailabilities: boolean,
    onSave?: (allowNotify?: boolean) => void,
    onSaveBlock?: (name: string | undefined) => void,
    onResend?: () => void,
    ageCategories?: AgeCategory[]
    onClose: () => void,
    onShowFolios?: () => void
    onShowLogs?: () => void
    serviceInfo?: Service
    onOpenCommunication?: () => void
    onOpenFormSelector?: () => void
    originalReservation?: Reservation
    selectedSlotType?: SlotType
    setSelectedSlotType?: (slotType: SlotType) => void
    rates?: Rate[] | undefined
    loading?: boolean
    onSaveAndSendNoShowLink?: () => void
    availabilityWarning?: React.ReactNode
}

const ReservationDetailsService = ({
                                       reservation,
                                       setReservation,
                                       // availabilityForActiveReservation,
                                       // loadingAvailabilities,
                                       onSave,
                                       onSaveBlock,
                                       onResend,
                                       ageCategories,
                                       onClose,
                                       onShowFolios,
                                       onShowLogs,
                                       serviceInfo,
                                       onOpenCommunication,
                                       onOpenFormSelector,
                                       originalReservation,
                                       selectedSlotType,
                                       setSelectedSlotType,
                                       rates,
                                       loading,
                                       onSaveAndSendNoShowLink,
                                       availabilityWarning
                                   }: Props) => {

    const {t} = useTranslation()
    const [canEdit, setCanEdit] = useState(true)
    const appData = useCurrentApp()
    const sector = useSector(reservation?.sectorId ?? appData?.configuration?.sectorId)
    const services = useServicesForSector(sector?.id, ServiceType.REGULAR)
    const resources = useAllRootResourcesForSector(sector?.id)
    const resourceCategories = useAllResourceCategoriesForSector(sector?.id)

    const appId = useCurrentAppId()
    const {
        resourceCategoryFilter,
    } = useReservationsState(appId)

    const resourcesWithAvailability =
        useResourcesWithAvailability({
            expectedStartDate: reservation?.expectedStartDate,
            expectedEndDate: reservation?.expectedEndDate,
            sectorId: sector?.id
        })


    /*
        Adjusting range dates to ensure that range doesn't interfere with the reservation dates
        So for the start date we intentionally set seconds to 59
        and for the end date we set seconds to 0
        This way we ensure that the reservation start date is not "smaller" than range end
        And that the reservation end date is not "greater" than range start :)
    */
    const adjustedRangeStartDate = useMemo(() => {
        if (!reservation?.expectedStartDate) return reservation?.expectedStartDate

        const date = new Date(reservation.expectedStartDate)
        date.setSeconds(59)
        return date
    }, [reservation?.expectedStartDate])

    const adjustedRangeEndDate = useMemo(() => {
        if (!reservation?.expectedEndDate) return reservation?.expectedEndDate

        const date = new Date(reservation.expectedEndDate)
        date.setSeconds(0)
        return date
    }, [reservation?.expectedEndDate])

    const nonCancelledStatuses = [
        ReservationStatus.PENDING,
        ReservationStatus.CONFIRMED,
        ReservationStatus.ACTIVE,
        ReservationStatus.COMPLETED,
        ReservationStatus.NO_SHOW,
        ReservationStatus.OPTION,
        ReservationStatus.UNCOMPLETED
    ]

    const nonCancelledResourceResQ = useResourceReservations(
        reservation?.resourceIds,
        adjustedRangeStartDate,
        adjustedRangeEndDate,
        sector?.id,
        nonCancelledStatuses
    )

    // Used for checking whether the block can be created during the selected time frame
    const nonCancelledResourceReservations = nonCancelledResourceResQ.data || []

    const resourceIdsKey = reservation?.resourceIds?.join(',')

    // Resources the service requirements picked for the user - released again when
    // another service is chosen, so only the manual picks survive a service switch
    const autoAssignedResourceIdsRef = useRef<string[]>([])

    useEffect(() => {
        if (reservation?.serviceId || !resourceIdsKey || !services.length || !resources.length) return

        const reservationResources = resources.filter(r => reservation?.resourceIds?.includes(r.id as string))
        const firstAssociatedService = services.find((service) =>
            reservationResources.some(resource => service.resourceIds?.includes(resource.id as string))
        )

        if (firstAssociatedService) {
            setReservation({
                ...reservation,
                serviceId: firstAssociatedService.id,
                // Keep the time frame chosen by the user (e.g. calendar drag-to-create);
                // only derive the end date from the service duration when none was set
                expectedEndDate: reservation?.expectedEndDate
                    ?? addMinutes(reservation?.expectedStartDate, firstAssociatedService.durationMinutes || 60)
            })
        }
    }, [services, resources, resourceIdsKey, reservation?.serviceId])

    const renderDateAndTime = () => {
        return (
            <Box
                flex={1}
            >
                <CDatePicker
                    value={reservation?.expectedStartDate}
                    marked={[
                        {
                            date: reservation?.expectedStartDate
                        }
                    ]}
                    onChange={(date) => {
                        //  updateField('date', date.value as Date)
                        setReservation({
                            ...reservation,
                            expectedStartDate: date
                        })
                    }}
                    disabled={!canEdit}
                    label={t('Date and time')}
                />
            </Box>
        )
    }

    const renderSlotType = () => {
        const color = getSlotTypeColor(selectedSlotType)
        const backgroundColor = getSlotTypeBackgroundColor(selectedSlotType)

        return (
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>

                <ToggleButtonGroup
                    id={'btn-slot-type'}
                    // By default, the slot type is RESERVATION
                    value={selectedSlotType || SlotType.RESERVATION}
                    exclusive
                    onChange={(e, value: SlotType) => {
                        if (value) {
                            setSelectedSlotType?.(value)
                        }
                    }}
                    size={'small'}
                >
                    {
                        // Showing only permitted slot types based on the selected resource category filter (Resources / Services dropdown)
                        getPermittedSlotTypesPerResourceCategoryFilter(resourceCategoryFilter).map((type) => (
                            <ToggleButton
                                key={type}
                                value={type}
                                disabled={!canEdit || nonCancelledResourceReservations.length > 0}
                                sx={{
                                    '&.Mui-selected': {
                                        color: color,
                                        backgroundColor: backgroundColor,
                                        border: `1px solid ${color}`
                                    },
                                    '&.Mui-selected:hover': {
                                        backgroundColor: backgroundColor
                                    }
                                }}
                            >
                                <Stack direction={'row'} alignItems={'center'}
                                       spacing={1}>

                                    <Typography
                                        variant={'body1'}
                                        sx={{whiteSpace: 'nowrap'}}
                                    >
                                        {t(`Slot type ${type}`)}
                                    </Typography>

                                    {getSlotTypeIcon(type)}

                                </Stack>


                            </ToggleButton>
                        ))
                    }
                </ToggleButtonGroup>

                {
                    // Don't allow creating BLOCK type if there are non-cancelled reservations involving the selected resources
                    nonCancelledResourceReservations.length > 0 &&
                    <Alert
                        severity={'warning'}
                    >
                        <Typography variant={'body1'}>
                            {t('Cannot create block as there is some reservation involving this resource')}
                        </Typography>
                    </Alert>
                }


            </Stack>
        )
    }

    const renderService = () => {
        // Find the resources in the current reservation
        const reservationResources = resources.filter(r => reservation?.resourceIds?.includes(r.id as string))

        // Iterate over all services and check if which services do have at least one resource in common with the reservation resources
        const associatedServices = services.filter((service) =>
            reservationResources?.some(resource =>
                service.resourceIds?.includes(resource.id as string)
            )
        )

        const notAssociatedServices = services.filter(
            service => !associatedServices.includes(service)
        )

        // Placing the services that have resources in common with the reservation resources on top of the list
        const sortedServices = [
            ...associatedServices,
            ...notAssociatedServices
        ]

        return (
            <Autocomplete
                sx={{
                    '& fieldset': {
                        height: '4em',
                    },
                    '& .MuiAutocomplete-input': {
                        height: '2.2em',
                        lineHeight: '2.2em',
                    },
                }}
                id={'service'}
                options={sortedServices}
                size={'small'}
                getOptionLabel={(option: Service) => serviceDetailedInfo(rates, option, reservation?.expectedStartDate)}
                value={services.find(s => s.id === reservation?.serviceId) || null}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        fullWidth
                        size={'small'}
                        label={t('Service')}
                    />
                )}

                groupBy={(option) =>
                    associatedServices.map(s => s.id).includes(option.id as string) ? t('Associated services') : t('Other services')
                }

                renderOption={(props, option: Service) => {

                    const backgroundColor = reservationResources?.some(resource =>
                        option.resourceIds?.includes(resource.id as string)
                    ) ? CommonThemeColors.sys.success.light
                        : CommonThemeColors.sys.error.light

                    return (
                        <Box component="li" {...props} key={option.id}
                             style={{display: 'flex', alignItems: 'center'}}>
                            <Chip
                                label={serviceDetailedInfo(rates, option, reservation?.expectedStartDate)}
                                style={{
                                    backgroundColor,
                                    color: 'black',
                                }}
                            />
                        </Box>
                    )
                }}

                fullWidth
                onChange={(e, value) => {
                    // Free the resources the previous service assigned before filling
                    // the requirements of the newly picked one
                    const keptResourceIds = releaseAutoAssignedResources(
                        reservation?.resourceIds,
                        autoAssignedResourceIdsRef.current
                    )

                    const {resourceIds, addedResourceIds} = autoAssignResourcesForService({
                        service: value || undefined,
                        resources,
                        selectedResourceIds: keptResourceIds,
                        resourcesWithAvailability
                    })

                    autoAssignedResourceIdsRef.current = addedResourceIds

                    setReservation({
                        ...reservation,
                        serviceId: value?.id,
                        resourceIds,
                        expectedEndDate: addMinutes(reservation?.expectedStartDate, value?.durationMinutes || 60)
                    })
                }}
            />
        )
    }

    const renderResources = () => {
        // Find the service associated with the current reservation
        const currentService = services.find(s => s.id === reservation?.serviceId)

        const linkedResources = resources.filter((resource) =>
            currentService?.resourceIds?.includes(resource.id as string)
        )

        const notLinkedResources = resources.filter(
            resource => !currentService?.resourceIds?.includes(resource.id as string)
        )

        const labeledResources = [
            ...linkedResources.map(resource => ({
                ...resource,
                group: t('Linked resources')
            })),
            ...notLinkedResources.map(resource => ({
                ...resource,
                group: t('Not linked resources')
            }))
        ]

        return (
            <Autocomplete
                sx={{
                    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                        minHeight: '4em',
                        paddingInline: '8px',
                    }
                }}
                id={'categories'}
                multiple

                // Sorting by availability & keeping the collection immutable
                options={[...(labeledResources || [])].sort((a, b) => {
                    const aAvailable = isResourceAvailable(a, resourcesWithAvailability, reservation)
                    const bAvailable = isResourceAvailable(b, resourcesWithAvailability, reservation)

                    if (aAvailable === bAvailable) return 0
                    return aAvailable ? -1 : 1
                })}

                groupBy={(option) =>
                    linkedResources.map(r => r.id).includes(option.id as string) ? t('Linked resources') : t('Not linked resources')
                }

                size={'small'}
                getOptionLabel={(option: Resource) => {
                    const categories = resourceCategories?.filter(c => option.categoryIds?.includes(c.id!))
                    const categoryNames = categories?.map(c => c.name).join(', ')
                    return [option.name || '', categoryNames].filter(Boolean).join(' - ')
                }}
                value={reservation?.resourceIds?.map(id => resources?.find(c => c.id === id)).filter(Boolean) as Resource[] || []}
                onOpen={() => {
                    // Force scroll to top when dropdown opens
                    setTimeout(() => {
                        const listbox = document.querySelector('#categories-listbox')
                        if (listbox) {
                            listbox.scrollTop = 0
                        }
                    }, 1)
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        fullWidth
                        size={'small'}
                        label={t('Resources')}
                        InputLabelProps={{
                            sx: {
                                transform: 'translate(14px, 16px) scale(1)',
                                '&.MuiInputLabel-shrink': {
                                    transform: 'translate(14px, -11px) scale(0.75)'
                                }
                            }
                        }}
                    />
                )}

                // Rendering options based on availability and resource attachment
                renderOption={(props, option: Resource) => {

                    const backgroundColor = isResourceAvailable(option, resourcesWithAvailability, reservation) ? CommonThemeColors.sys.success.light
                        : CommonThemeColors.sys.error.light

                    return (
                        <Box component="li" {...props} key={option.id}
                             style={{display: 'flex', alignItems: 'center'}}>
                            <Chip
                                label={option.name}
                                style={{
                                    backgroundColor,
                                    color: 'black',
                                }}
                            />
                        </Box>
                    );
                }}

                renderGroup={params => (
                    <li key={params.key}>
                        <div style={{
                            fontWeight: 'bold',
                            padding: 10
                        }}>{params.group}</div>
                        <ul style={{padding: 5}}>{params.children}</ul>
                    </li>
                )}
                fullWidth
                onChange={(e, value) => {
                    const newResources = [...value.map((v: any) => v.id)]

                    // Anything the user touches becomes his own pick and must not be
                    // released by the next service change
                    autoAssignedResourceIdsRef.current = autoAssignedResourceIdsRef.current
                        .filter(id => newResources.includes(id))

                    setReservation({
                        ...reservation,
                        resourceIds: newResources
                    })
                }}
                getOptionDisabled={(option) => reservation?.resourceIds?.includes(option.id as string) as boolean}
            />
        )
    }

    return (
        <ReservationDetailsWrapper
            reservation={reservation}
            setReservation={setReservation}
            onSave={onSave}
            onSaveBlock={onSaveBlock}
            onResend={onResend}
            ageCategories={ageCategories}
            resources={resources}
            availabilityWarning={availabilityWarning}
            onClose={onClose}
            onShowFolios={onShowFolios}
            onShowLogs={onShowLogs}
            serviceInfo={serviceInfo}
            onOpenCommunication={onOpenCommunication}
            onOpenFormSelector={onOpenFormSelector}
            originalReservation={originalReservation}

            slotTypeToggle={(
                <Grid item xs={12} md={12} sx={{alignSelf: 'center'}}>
                    {renderSlotType()}
                </Grid>
            )}

            position1Content={(
                <Grid item xs={12} md={12} sx={{alignSelf: 'center'}}>
                    {renderService()}
                </Grid>
            )}
            position2Content={(
                <Grid item xs={12} md={12} sx={{alignSelf: 'center'}}>
                    {renderResources()}
                </Grid>
            )}
            selectedSlotType={selectedSlotType}
            loading={loading}
            onSaveAndSendNoShowLink={onSaveAndSendNoShowLink}
        >
        </ReservationDetailsWrapper>
    )

}
export default ReservationDetailsService;
